import classNames from 'classnames';
import { IconSize } from '@components/icon';
import { Screen, Stack } from '@components/layout';
import { FooterLegalMenu, FooterLegalObjectProps } from '@components/nav/FooterLegalMenu';
import { FooterList, FooterListProps } from '@components/nav/FooterList';
import { FooterSiteIndex } from '@components/nav/FooterSiteIndex';
import { Logo } from '@components/nav/Logo';
import { SocialIcons, SocialIconsProps } from '@components/nav/SocialIcons';
import { Body } from '@components/text';
import { FooterDisclaimer } from '../FooterDisclaimer';
import './Footer.scss';

export interface FooterProps {
  copyrightText?: string | string[];
  globalMenu?: Array<FooterLegalObjectProps>;
  sections?: Array<FooterListProps>;
  seoText?: string | string[];
  socials?: SocialIconsProps;
  className?: string;
  variant?: 'left' | 'center';
  brand?: 'pplus' | 'pluto';
  linkComponent?: React.ElementType;
  disclaimerProps?: Omit<React.ComponentProps<typeof FooterDisclaimer>, 'text' | 'align'>;
  logoHref?: string;
}

/**
 * Footer
 * @param copyrightText - site disclaimer; pass an array to render multiple lines (e.g. legal copy)
 * @param globalMenu - array of objects used in the FooterLegalMenu links
 * @param sections - array of objects used in the FooterList sections; each section may set `href` to make its heading a clickable link
 * @param seoText - optional SEO text displayed under the FooterList sections; pass an array to render multiple lines
 * @param socials - array of objects used in SocialIcons
 * @param variant - layout alignment variant ('left' or 'center')
 * @param brand - brand variant for logo ('pplus' or 'pluto')
 * @param linkComponent - Custom component to render links instead of a native `<a>` (e.g. Next.js Link, React Router Link).
 * @param disclaimerProps - Props to be passed to the FooterDisclaimer component.
 * @param logoHref - URL for the logo link. When provided, the logo becomes a clickable link.
 */
export const Footer: React.FC<FooterProps> = ({
  copyrightText = '© 2024 Paramount. All Rights Reserved.',
  globalMenu = [],
  sections,
  seoText,
  socials,
  className,
  variant = 'left',
  brand = 'pplus',
  linkComponent,
  disclaimerProps,
  logoHref,
  ...rest
}) => {
  const centered = variant === 'center';
  const socialIconSize: IconSize = centered ? 'medium-small' : 'large';

  const disclaimer = (
    <FooterDisclaimer
      text={copyrightText}
      align={centered ? 'center' : 'start'}
      {...disclaimerProps}
      className={classNames('vds-footer__disclaimer', disclaimerProps?.className)}
    />
  );
  const seoItems = seoText ? (Array.isArray(seoText) ? seoText : [seoText]) : [];
  const seo: React.ReactNode = seoItems.length > 0 && brand === 'pplus' && (
    <Stack className="vds-footer__seo" flexDirection="column" gap="spacing-1">
      {seoItems.map((item, index) => (
        <Body
          key={index}
          size="small"
          variant="caption"
          color="tertiary"
          align={centered ? 'center' : 'start'}>
          {item}
        </Body>
      ))}
    </Stack>
  );
  const footerLists = sections?.map((section, index) => (
    <FooterList
      {...section}
      key={section.label || section.href || `section-${index}`}
      className="vds-footer__list"
      brand={brand}
      linkComponent={linkComponent}
    />
  ));

  return (
    <footer className={classNames('vds-footer', `vds-footer--${variant}`, className)} {...rest}>
      <Screen className="vds-footer__screen">
        <Logo
          brand={brand}
          className="vds-footer__logo"
          href={logoHref}
          linkComponent={linkComponent}
        />
        <div className="vds-footer--default">
          <div className="vds-footer--list-wrapper">
            {footerLists}
            {socials && (
              <SocialIcons
                label={socials.label}
                items={socials.items}
                className="vds-footer__socials"
                size={socialIconSize}
                centered={centered}
                linkComponent={linkComponent}
              />
            )}
          </div>
          {seo}
          {brand === 'pluto' && (
            <FooterLegalMenu
              className="vds-footer__legal"
              globalMenu={globalMenu}
              hasDivider={false}
              weight="bold"
              centered={centered}
              linkComponent={linkComponent}
            />
          )}
          {disclaimer}
        </div>

        <div className="vds-footer--mobile">
          {socials && (
            <SocialIcons
              label={socials.label}
              items={socials.items}
              size={socialIconSize}
              className="vds-footer__socials"
              centered={centered}
              linkComponent={linkComponent}
            />
          )}
          {brand === 'pluto' && (
            <div className="vds-footer__legal-mobile-wrapper">
              <FooterLegalMenu
                className="vds-footer__legal-mobile"
                globalMenu={globalMenu}
                hasDivider={false}
                weight="bold"
                centered={centered}
                linkComponent={linkComponent}
              />
            </div>
          )}
          {seo}
          {disclaimer}
          <FooterSiteIndex className="vds-footer__site-index">{footerLists}</FooterSiteIndex>
        </div>
      </Screen>
    </footer>
  );
};
